import { ArrowRight2 } from 'iconsax-react';
import { isEmpty } from 'lodash-es';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import TagItem, { PrefixType } from '@/components/tags/tag-item';
import useAdjustTagsByFriend from '@/hooks/tags/use-adjust-tags-by-friend';
import useCreateTag from '@/hooks/tags/use-create-tag';
import useTagReducer from '@/hooks/tags/use-tag-reducer';
import useTags, { useTagsByUserId } from '@/hooks/tags/use-tags';
import ROUTES from '@/routes';

export default function UserTagsRoute() {
  const { t } = useTranslation();
  const { userId } = useTypedParams(ROUTES.CONTACT.USERS.BY_ID.TAGS);
  const { tags } = useTags();
  const { tags: currentTags } = useTagsByUserId(userId);
  const { mutateAsync: adjustTags, isPending } = useAdjustTagsByFriend(userId);
  const { mutateAsyncWithPrompt: createTag } = useCreateTag();
  const { addedTags, dispatch, adjustments, toggleTag, hasAdjustments, removedTags } = useTagReducer();

  useEffect(() => {
    if (currentTags) {
      dispatch({ action: 'set-current-tags', tags: currentTags });
    }
  }, [currentTags, dispatch]);

  const handleUpdateTags = async () => {
    await adjustTags(adjustments);
    dispatch({ action: 'reset-adjustments' });
  };

  return (
    <PageLayout
      title={t('contact.tags.add-tag')}
      headerConfig={{ navigateBack: true }}>
      <div className='px-5'>
        <div className='text-body1 flex items-center justify-between py-2'>
          <span className='font-bold'>{t('contact.tags.current-tags')}</span>

          {(!isEmpty(currentTags) || !isEmpty(adjustments)) && (
            <ProgressButton
              as='button'
              disabled={isPending || !hasAdjustments}
              className='btn btn-ghost text-primary btn-sm'
              label='common.confirm'
              onClick={handleUpdateTags}
            />
          )}
        </div>
        <div className='flex gap-2 flex-wrap py-2'>
          {isEmpty(currentTags) && isEmpty(addedTags) && (
            <span className='text-secondary'> {t('contact.tags.no-tags')}</span>
          )}
          {currentTags.map((tag) => {
            const isRemoved = removedTags.some((removedTag) => removedTag.Id === tag.Id);

            return (
              <TagItem
                key={`current-tag-${tag.Id}`}
                tag={tag}
                prefix={PrefixType.remove}
                variant={isRemoved ? 'inverse-secondary' : 'secondary'}
                onClick={(tag) => toggleTag(tag)}
              />
            );
          })}
          {addedTags.map((tag) => (
            <TagItem
              key={`added-tag-${tag.Id}`}
              tag={tag}
              prefix={PrefixType.check}
              variant='primary'
              onClick={(tag) => toggleTag(tag)}
            />
          ))}
        </div>
      </div>

      <div className='px-5'>
        <div className='text-body1 flex justify-between items-center py-2'>
          <span className='font-bold'>{t('contact.tags.all-tags')}</span>
          <Link
            className='btn btn-ghost pe-0 btn-sm text-secondary'
            to={ROUTES.CONTACT.TAGS.$path()}>
            {t('common.edit')}
            <ArrowRight2 className='size-4' />
          </Link>
        </div>

        <div className='flex gap-2 flex-wrap py-2'>
          {currentTags.map((tag) => (
            <TagItem
              key={`all-current-tag-${tag.Id}`}
              tag={tag}
              variant='secondary'
              prefix={PrefixType.none}
            />
          ))}

          {tags
            .filter((tag) => !currentTags.some((t) => t.Id === tag.Id))
            .map((tag) => {
              const isAdded = addedTags.some((addedTag) => addedTag.Id === tag.Id);

              return (
                <TagItem
                  key={`all-tag-${tag.Id}`}
                  tag={tag}
                  variant={isAdded ? 'primary' : 'outlined'}
                  prefix={isAdded ? PrefixType.check : PrefixType.add}
                  onClick={(tag) => toggleTag(tag)}
                />
              );
            })}

          <TagItem
            key='create-new-tag'
            className='border-primary text-primary'
            tag={{ Id: -1, TagName: t('common.add'), TagCount: 0, TagMember: [] }}
            variant='outlined'
            prefix={PrefixType.add}
            onClick={() => createTag(true)}
          />
        </div>
      </div>
    </PageLayout>
  );
}
